import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Lock, Phone, UserPlus, Shield } from 'lucide-react';
import InitializationService from '../services/initializationService';
import AuthForm from './AuthForm';
import '../Login.css';

/**
 * Composant pour l'initialisation de l'application
 * Affiche un formulaire pour créer le premier super administrateur
 */
function InitialSetup() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: ''
  });
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Effacer l'erreur de validation pour ce champ
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    // Validation du prénom
    if (!formData.firstName.trim()) {
      errors.firstName = 'Le prénom est requis';
    }

    // Validation du nom
    if (!formData.lastName.trim()) {
      errors.lastName = 'Le nom est requis';
    }

    // Validation de l'email
    if (!formData.email.trim()) {
      errors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Format d\'email invalide';
    }

    // Validation du mot de passe
    if (!formData.password) {
      errors.password = 'Le mot de passe est requis';
    } else if (formData.password.length < 6) {
      errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }

    // Validation de la confirmation du mot de passe
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Valider le formulaire
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Créer le premier super administrateur
      const result = await InitializationService.createFirstSuperAdmin({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone
      });

      if (result.success) {
        // Attendre un peu que toutes les données soient bien synchronisées
        console.log('✅ Initialisation réussie, attente de synchronisation...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Recharger la page pour que l application détecte le nouvel utilisateur
        console.log('✅ Rechargement de l application...');
        window.location.href = '/dashboard';
      }
    } catch (error) {
      console.error('Erreur lors de l\'initialisation:', error);
      setError(error.error || error.message || 'Une erreur est survenue lors de la création du compte');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthForm title="Initialisation de l'application">
      <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="text-purple-600" size={24} />
          <h3 className="text-lg font-semibold text-purple-900">
            Configuration de votre espace Agentime
          </h3>
        </div>
        <p className="text-sm text-purple-700">
          Votre abonnement Agentime est activé ! Créez votre compte administrateur principal pour configurer votre organisation et accéder à toutes les fonctionnalités.
        </p>
        <p className="text-xs text-purple-600 mt-2">
          <strong>Important :</strong> Ce compte administrateur aura un accès complet à votre plateforme et pourra gérer les équipes, les bénéficiaires et les paramètres de votre organisation.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="auth-form">
        <div className="grid grid-cols-2 gap-4">
          {/* Prénom */}
          <div className="input-group">
            <label htmlFor="firstName" className="input-label">
              <User className="input-icon" />
              <span>Prénom *</span>
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              className={`input-field ${validationErrors.firstName ? 'border-red-500' : ''}`}
              placeholder="Jean"
            />
            {validationErrors.firstName && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.firstName}</p>
            )}
          </div>

          {/* Nom */}
          <div className="input-group">
            <label htmlFor="lastName" className="input-label">
              <User className="input-icon" />
              <span>Nom *</span>
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              className={`input-field ${validationErrors.lastName ? 'border-red-500' : ''}`}
              placeholder="Dupont"
            />
            {validationErrors.lastName && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.lastName}</p>
            )}
          </div>
        </div>

        {/* Email */}
        <div className="input-group">
          <label htmlFor="email" className="input-label">
            <Mail className="input-icon" />
            <span>Email professionnel *</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className={`input-field ${validationErrors.email ? 'border-red-500' : ''}`}
            placeholder="admin@votre-entreprise.fr"
          />
          {validationErrors.email && (
            <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>
          )}
        </div>

        {/* Téléphone (optionnel) */}
        <div className="input-group">
          <label htmlFor="phone" className="input-label">
            <Phone className="input-icon" />
            <span>Téléphone</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="input-field"
            placeholder="06 12 34 56 78"
          />
        </div>

        {/* Mot de passe */}
        <div className="input-group">
          <label htmlFor="password" className="input-label">
            <Lock className="input-icon" />
            <span>Mot de passe *</span>
          </label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            className={`input-field ${validationErrors.password ? 'border-red-500' : ''}`}
            placeholder="Minimum 6 caractères"
          />
          {validationErrors.password && (
            <p className="text-red-500 text-xs mt-1">{validationErrors.password}</p>
          )}
        </div>

        {/* Confirmation mot de passe */}
        <div className="input-group">
          <label htmlFor="confirmPassword" className="input-label">
            <Lock className="input-icon" />
            <span>Confirmer le mot de passe *</span>
          </label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
            className={`input-field ${validationErrors.confirmPassword ? 'border-red-500' : ''}`}
            placeholder="Répétez le mot de passe"
          />
          {validationErrors.confirmPassword && (
            <p className="text-red-500 text-xs mt-1">{validationErrors.confirmPassword}</p>
          )}
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="auth-button flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Création en cours...</span>
            </>
          ) : (
            <>
              <UserPlus size={20} />
              <span>Créer le compte super administrateur</span>
            </>
          )}
        </button>

        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-600 text-center">
            Ce compte aura un accès complet à toutes les fonctionnalités de l'application
          </p>
        </div>
      </form>
    </AuthForm>
  );
}

export default InitialSetup;
